import { useSyncExternalStore, type ReactNode } from "react";
import { Bot, FlaskConical, Globe, History } from "lucide-react";
import { Link, NavLink } from "react-router-dom";

import { PageHeader } from "@/shared/ui/PageHeader";
import { cn } from "@/shared/lib/utils";

import { isWebsiteFixtureData, subscribeWebsiteFixtureFlag } from "../api/websiteService";

/**
 * 建站舱外壳(2026-09-22,形制照 `acquisition/ui/AcqShell`):页头 + 样例横幅 + 主 tab + 二级入口。
 *
 * 主 tab 只有两页(我的站点 / 模板市场);站点级三页(采集 / 编辑 / 域名)走 `SiteShell`,
 * 那三页的上级是「某个站点」不是「舱」,页头要带返回和站点名。
 */
// 1009 C 线:只写已经做好的能力(与 api V18 catalog 介绍同口径);官网 AI 客服走客服舱「网页 / H5」接入点,见右上入口。
// 不写「HTTPS」:模拟发布(stub)下整页不许出现证书 / HTTPS 字样(附录 A.38)。
const SUBTITLE = "选模板一键出初稿:全站自动配图、产品详情同步、新闻舱定期发文、绑定自有域名";

const TABS = [
  { to: "/ai-website", label: "我的站点", end: true },
  { to: "/ai-website/templates", label: "模板市场", end: false },
];

export function WebsiteShell({
  subtitle = SUBTITLE,
  actions,
  children,
}: {
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    // ★ 裸 space-y-6,不带自己的 p-*:页面留白由 AdminLayout 的 main 统一给(获客舱同款教训)。
    <div className="space-y-6">
      <PageHeader icon={Globe} title="建站舱" subtitle={subtitle} actions={actions} />

      <FixtureBanner />

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b">
        <nav className="-mb-px flex min-w-0 gap-1 overflow-x-auto" aria-label="建站舱">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                cn(
                  "whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold transition-colors",
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex shrink-0 items-center gap-1 pb-1.5">
          {/*
            1010:客服舱「网页 / H5」渠道已上线 —— 官网 AI 客服 = 在渠道管理建接入点、把嵌入代码贴到官网。
            1014:建站舱官网已能在「上线」页一键自动挂客服浮窗(宿主 chatWidget);这个入口留给手贴代码的其他官网。
          */}
          <Link
            to="/channels?tab=web"
            data-testid="website-kefu-entry"
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <Bot className="h-3.5 w-3.5 shrink-0" />
            官网 AI 客服 → 在渠道管理建接入点,复制嵌入代码贴到官网
          </Link>
          {/* 发布记录没有独立页,就是「我的站点」右栏那一列动态(锚点 #activity)。 */}
          <Link
            to="/ai-website#activity"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <History className="h-3.5 w-3.5" />
            发布记录
          </Link>
        </div>
      </div>

      {children}
    </div>
  );
}

/**
 * ★★「样例数据」横幅 —— fixture 回落**不许静默**(0916 纪律,文案与获客舱同款)。
 * 旗子只从 false → true,刷新即清;`useSyncExternalStore` 订阅式读,别的页面触发的那一次也不漏。
 * 站点级三页的 `SiteShell` 也挂它 —— 采集 / 内容 / 域名在 dev 下同样会回桩。
 */
export function FixtureBanner() {
  const on = useSyncExternalStore(subscribeWebsiteFixtureFlag, isWebsiteFixtureData, () => false);
  if (!on) return null;
  return (
    <div className="flex items-center gap-2 rounded-lg border border-warning/50 bg-warning/10 px-3 py-2 text-xs font-semibold text-warning">
      <FlaskConical className="h-4 w-4 shrink-0" />
      样例数据 —— 这一页至少有一口没连上后端，走的是本地桩。上面的公司、条数和价钱都不是真的。
    </div>
  );
}
